import React, { useState, useEffect } from 'react';
import {
  FileBarChart,
  Download,
  Printer,
  Calendar,
  Building,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Coffee,
  Sparkles,
  RefreshCw,
  Users,
  Monitor,
  ShieldCheck,
  ChevronRight,
  Flame,
  FileText,
} from 'lucide-react';
import { api } from '../../lib/api';
import { IosSegmentedControl, SegmentOption } from '../../components/ios/IosSegmentedControl';
import { ReportModal } from '../../components/ReportModal';
import { haptics } from '../../lib/haptics';
import { sounds } from '../../lib/sounds';

type RangeOption = 'today' | 'yesterday' | '7d' | '30d' | 'all';

interface DepartmentMetric {
  department: string;
  totalEvents: number;
  totalInactiveSeconds: number;
  activeEmployees: number;
}

interface HourlyDistribution {
  hour: number;
  label: string;
  eventCount: number;
  totalInactiveSeconds: number;
}

export function ReportsPage() {
  const [range, setRange] = useState<RangeOption>('today');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportingShiftCsv, setExportingShiftCsv] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const [data, setData] = useState<{
    range: string;
    totalDevices: number;
    totalUsers: number;
    summary: {
      totalEvents: number;
      totalInactiveSeconds: number;
      longestInactiveSeconds: number;
      avgInactiveSeconds: number;
    };
    departmentBreakdown: DepartmentMetric[];
    hourlyDistribution: HourlyDistribution[];
    shiftOfflineReport?: {
      isShiftActive: boolean;
      isWorkDay: boolean;
      shiftHoursStart: string;
      shiftHoursEnd: string;
      timezone: string;
      workDays: string;
      totalEnrolled: number;
      offlineInShiftCount: number;
      onlineInShiftCount: number;
      shiftComplianceRate: number;
      devices: Array<{
        id: string;
        deviceId: string;
        deviceName: string;
        hostname: string;
        userName: string;
        userEmail: string;
        userDepartment: string;
        status: string;
        lastSeenAt: string | null;
        offlineMinutes: number;
        minutesSinceLastSeen: number;
        seenTodayInShift: boolean;
        classification: string;
        classificationLabel: string;
      }>;
    };
    recentEvents: any[];
  } | null>(null);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.admin.getReportsAnalytics({ range });
      setData(res);
    } catch (err: any) {
      setError(err.message || 'Failed to generate reports analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [range]);

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      haptics.impact();
      sounds.playTapSound();
      await api.admin.exportActivity({ range });
      sounds.playChimeSound();
    } catch (err: any) {
      alert(err.message || 'CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportShiftOfflineCsv = async () => {
    try {
      setExportingShiftCsv(true);
      haptics.impact();
      sounds.playTapSound();
      await api.admin.exportShiftOfflineCsv();
      sounds.playChimeSound();
    } catch (err: any) {
      alert(err.message || 'Shift offline export failed');
    } finally {
      setExportingShiftCsv(false);
    }
  };

  const handlePrint = () => {
    haptics.selection();
    window.print();
  };

  const formatSeconds = (sec: number) => {
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    const seconds = sec % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const rangeOptions: SegmentOption<RangeOption>[] = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: '7d', label: 'Last 7 Days' },
    { id: '30d', label: 'Last 30 Days' },
    { id: 'all', label: 'All Time' },
  ];

  const totalInactiveSecs = data?.summary.totalInactiveSeconds || 0;
  const totalEvents = data?.summary.totalEvents || 0;
  const totalEmployees = data?.totalUsers || 1;

  // Assume standard 8-hour workday per employee for range
  const daysMultiplier = range === 'today' || range === 'yesterday' ? 1 : range === '7d' ? 7 : range === '30d' ? 30 : 60;
  const theoreticalWorkSeconds = Math.max(3600, totalEmployees * 8 * 3600 * daysMultiplier);
  const activeSeconds = Math.max(0, theoreticalWorkSeconds - totalInactiveSecs);
  const efficiencyRate = Math.min(100, Math.max(0, Math.round((activeSeconds / theoreticalWorkSeconds) * 1000) / 10));

  // Hourly peak calculation
  const maxHourlyEvents = Math.max(1, ...(data?.hourlyDistribution.map((h) => h.eventCount) || [1]));

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full min-w-0 animate-ios-enter print:p-0 print:space-y-4">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white tracking-tight flex items-center gap-2">
              <span>Executive Reports & Intelligence</span>
              <Sparkles className="w-5 h-5 text-pulse-blue" />
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
              AUDIT STUDIO
            </span>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-medium">
            Fleet-wide productivity indices, departmental attendance distributions, and break compliance audits
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              haptics.selection();
              setIsReportModalOpen(true);
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-xs font-bold text-white shadow-md shadow-blue-500/20 ios-press transition"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Generate PDF Dossier</span>
          </button>

          <button
            onClick={handleExportCsv}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 text-xs font-semibold text-zinc-800 dark:text-zinc-200 border border-black/10 dark:border-white/10 shadow-sm ios-press transition disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5 text-zinc-500" />
            <span>{exporting ? 'Exporting...' : 'CSV Export'}</span>
          </button>

          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 text-xs font-semibold text-zinc-800 dark:text-zinc-200 border border-black/10 dark:border-white/10 shadow-sm ios-press transition"
            title="Print Presentation Mode"
          >
            <Printer className="w-3.5 h-3.5 text-zinc-500" />
            <span>Print View</span>
          </button>

          <button
            onClick={() => {
              haptics.tap();
              sounds.playTapSound();
              fetchReports();
            }}
            disabled={loading}
            className="p-2 rounded-xl bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border border-black/10 dark:border-white/10 shadow-sm ios-press disabled:opacity-50"
            title="Refresh Analytics"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Date Range Selector Pill */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 print:hidden">
        <IosSegmentedControl
          options={rangeOptions}
          value={range}
          onChange={(r) => setRange(r)}
          className="w-full sm:w-auto"
        />

        <div className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-blue-500" />
          <span>Reporting Period: <strong>{range.toUpperCase()}</strong></span>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-3.5 bg-rose-500/10 border border-rose-500/25 rounded-2xl text-xs text-rose-500 dark:text-rose-400 animate-ios-enter">
          {error}
        </div>
      )}

      {/* Executive KPI Scorecard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Productivity Rate */}
        <div className="p-5 rounded-3xl ios-card bg-emerald-500/[0.04] dark:bg-emerald-950/20 border-emerald-500/20 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                Fleet Productivity Rate
              </span>
              <span className="p-1.5 rounded-xl bg-emerald-500/15 text-emerald-500">
                <TrendingUp className="w-4 h-4" />
              </span>
            </div>
            <div className="text-3xl sm:text-4xl font-black text-emerald-600 dark:text-emerald-300 mt-2 tracking-tight">
              {efficiencyRate}%
            </div>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-3 font-medium">
            Active workstation focus vs recorded idle intervals
          </p>
        </div>

        {/* Total Inactivity Hours */}
        <div className="p-5 rounded-3xl ios-card bg-amber-500/[0.04] dark:bg-amber-950/20 border-amber-500/20 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                Cumulative Inactive Time
              </span>
              <span className="p-1.5 rounded-xl bg-amber-500/15 text-amber-500">
                <Clock className="w-4 h-4" />
              </span>
            </div>
            <div className="text-3xl sm:text-4xl font-black text-amber-600 dark:text-amber-300 mt-2 tracking-tight">
              {formatSeconds(totalInactiveSecs)}
            </div>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-3 font-medium">
            Across {totalEvents} detected idle periods
          </p>
        </div>

        {/* Average Idle Duration */}
        <div className="p-5 rounded-3xl ios-card flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                Average Incident Length
              </span>
              <span className="p-1.5 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-500">
                <AlertTriangle className="w-4 h-4" />
              </span>
            </div>
            <div className="text-3xl sm:text-4xl font-black text-zinc-900 dark:text-white mt-2 tracking-tight">
              {formatSeconds(data?.summary.avgInactiveSeconds || 0)}
            </div>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-3 font-medium">
            Longest recorded: {formatSeconds(data?.summary.longestInactiveSeconds || 0)}
          </p>
        </div>

        {/* Fleet Enrolled Coverage */}
        <div className="p-5 rounded-3xl ios-card flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                Enrolled Fleet Scope
              </span>
              <span className="p-1.5 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-500">
                <Monitor className="w-4 h-4" />
              </span>
            </div>
            <div className="text-3xl sm:text-4xl font-black text-zinc-900 dark:text-white mt-2 tracking-tight">
              {data?.totalDevices || 0} PCs
            </div>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-3 font-medium">
            {data?.totalUsers || 0} registered organization members
          </p>
        </div>
      </div>

      {/* Visual Section: Departmental Breakdown & Hourly Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Department Productivity Matrix */}
        <div className="p-6 rounded-3xl ios-card space-y-4">
          <div className="flex items-center justify-between border-b border-black/5 dark:border-white/5 pb-3">
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-blue-500" />
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white uppercase tracking-wider">
                Departmental Productivity Matrix
              </h3>
            </div>
            <span className="text-[10px] font-mono text-zinc-400">Team Rollup</span>
          </div>

          {!data || data.departmentBreakdown.length === 0 ? (
            <div className="py-12 text-center text-xs text-zinc-400 font-mono">
              No departmental inactivity recorded for this period.
            </div>
          ) : (
            <div className="space-y-3.5">
              {data.departmentBreakdown.map((dept, idx) => {
                const maxSecs = Math.max(1, ...(data.departmentBreakdown.map((d) => d.totalInactiveSeconds) || [1]));
                const fillPercent = Math.min(100, Math.max(8, Math.round((dept.totalInactiveSeconds / maxSecs) * 100)));

                return (
                  <div key={idx} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-zinc-800 dark:text-zinc-200 flex items-center gap-2">
                        <span>{dept.department}</span>
                        <span className="text-[10px] font-mono text-zinc-400 font-normal">
                          ({dept.totalEvents} incidents)
                        </span>
                      </span>
                      <span className="font-mono font-bold text-zinc-900 dark:text-white">
                        {formatSeconds(dept.totalInactiveSeconds)}
                      </span>
                    </div>

                    {/* Comparative Bar */}
                    <div className="h-3 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
                        style={{ width: `${fillPercent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Hour-of-Day Inactivity Heatmap */}
        <div className="p-6 rounded-3xl ios-card space-y-4">
          <div className="flex items-center justify-between border-b border-black/5 dark:border-white/5 pb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white uppercase tracking-wider">
                Time-of-Day Inactivity Distribution
              </h3>
            </div>
            <span className="text-[10px] font-mono text-zinc-400">24-Hour Fleet Heatmap</span>
          </div>

          <div className="space-y-2">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Identifies peak fleet-wide drop-offs during shift hours (e.g. lunch periods, late-afternoon dips).
            </p>

            {/* 24-Hour Column Chart */}
            <div className="pt-4 flex items-end justify-between gap-1 h-36 w-full">
              {data?.hourlyDistribution.map((h, idx) => {
                const heightPercent = Math.min(100, Math.max(6, Math.round((h.eventCount / maxHourlyEvents) * 100)));
                const isHigh = h.eventCount >= maxHourlyEvents * 0.7 && h.eventCount > 0;

                return (
                  <div
                    key={idx}
                    className="flex-1 flex flex-col items-center gap-1 group relative h-full justify-end"
                  >
                    {/* Tooltip on hover */}
                    <div className="absolute -top-8 hidden group-hover:flex px-2 py-1 bg-zinc-900 text-white rounded text-[10px] font-mono whitespace-nowrap z-20 shadow-lg">
                      {h.label}: {h.eventCount} alerts ({formatSeconds(h.totalInactiveSeconds)})
                    </div>

                    <div
                      className={`w-full rounded-t transition-all duration-300 ${
                        isHigh
                          ? 'bg-rose-500 shadow-md shadow-rose-500/20'
                          : h.eventCount > 0
                          ? 'bg-blue-500'
                          : 'bg-zinc-200 dark:bg-zinc-800'
                      }`}
                      style={{ height: `${heightPercent}%` }}
                    />
                    <span className="text-[8px] font-mono text-zinc-400 select-none">
                      {idx % 4 === 0 ? h.hour : ''}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between text-[10px] font-mono text-zinc-500 pt-2 border-t border-black/5 dark:border-white/5">
              <span>00:00 (Midnight)</span>
              <span>08:00 (Morning Shift)</span>
              <span>13:00 (Lunch Dip)</span>
              <span>18:00 (Evening)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Shift Absence & Offline Workstation Intelligence */}
      <div className="p-6 rounded-3xl ios-card space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-black/5 dark:border-white/5 pb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-rose-500/10 border border-rose-500/25 flex items-center justify-center text-rose-500">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-bold text-zinc-900 dark:text-white uppercase tracking-wider">
                  Shift Absence & Offline Workstations
                </h3>
                {data?.shiftOfflineReport?.isShiftActive ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span>SHIFT ACTIVE ({data.shiftOfflineReport.shiftHoursStart} – {data.shiftOfflineReport.shiftHoursEnd})</span>
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-zinc-500/15 text-zinc-600 dark:text-zinc-400 border border-zinc-500/30">
                    SHIFT OFF-DUTY ({data?.shiftOfflineReport?.shiftHoursStart || '09:00'} – {data?.shiftOfflineReport?.shiftHoursEnd || '19:00'})
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                Audit of enrolled workstations that are offline or unresponsive during company operational hours
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleExportShiftOfflineCsv}
              disabled={exportingShiftCsv}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-xs font-bold text-zinc-800 dark:text-zinc-200 border border-black/5 dark:border-white/5 transition ios-press disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5 text-zinc-500" />
              <span>{exportingShiftCsv ? 'Exporting...' : 'Export Shift Report (CSV)'}</span>
            </button>
          </div>
        </div>

        {/* 3 Metric Pills */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 border border-black/5 dark:border-white/5">
            <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider block">
              Offline During Shift
            </span>
            <div className={`text-2xl font-black mt-1 ${
              (data?.shiftOfflineReport?.offlineInShiftCount || 0) > 0 ? 'text-rose-500' : 'text-emerald-500'
            }`}>
              {data?.shiftOfflineReport?.offlineInShiftCount || 0} Workstations
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">
              Out of {data?.shiftOfflineReport?.totalEnrolled || 0} enrolled devices
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 border border-black/5 dark:border-white/5">
            <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider block">
              In-Shift Fleet Compliance
            </span>
            <div className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">
              {data?.shiftOfflineReport?.shiftComplianceRate ?? 100}%
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">
              {data?.shiftOfflineReport?.onlineInShiftCount || 0} machines reporting live
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 border border-black/5 dark:border-white/5">
            <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider block">
              Shift Policy Window
            </span>
            <div className="text-lg font-bold text-zinc-900 dark:text-white mt-1">
              {data?.shiftOfflineReport?.shiftHoursStart || '09:00'} – {data?.shiftOfflineReport?.shiftHoursEnd || '19:00'}
            </div>
            <p className="text-[11px] text-zinc-500 mt-1 font-mono">
              Timezone: {data?.shiftOfflineReport?.timezone || 'Asia/Kolkata'}
            </p>
          </div>
        </div>

        {/* List of Offline Workstations */}
        {(!data?.shiftOfflineReport || data.shiftOfflineReport.devices.length === 0) ? (
          <div className="py-8 px-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 text-center">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center mx-auto mb-2">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <h4 className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
              Full Shift Presence: No Offline Devices Detected
            </h4>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              All enrolled fleet workstations are actively online and communicating during this operational shift.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-black/5 dark:border-white/5">
            <table className="w-full text-left text-xs">
              <thead className="bg-zinc-50 dark:bg-zinc-900/80 text-zinc-500 uppercase font-mono text-[10px] border-b border-black/5 dark:border-white/5">
                <tr>
                  <th className="py-3 px-4">Workstation</th>
                  <th className="py-3 px-4">Assigned Employee</th>
                  <th className="py-3 px-4">Department</th>
                  <th className="py-3 px-4">Last Seen</th>
                  <th className="py-3 px-4">Offline in Shift</th>
                  <th className="py-3 px-4">Absence Classification</th>
                  <th className="py-3 px-4 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5 dark:divide-white/5">
                {data.shiftOfflineReport.devices.map((dev, idx) => (
                  <tr key={idx} className="hover:bg-zinc-50/50 dark:hover:bg-zinc-900/40 transition">
                    <td className="py-3 px-4 font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                      <Monitor className="w-3.5 h-3.5 text-zinc-400" />
                      <span>{dev.deviceName}</span>
                      <span className="text-[10px] font-mono text-zinc-400 font-normal">({dev.hostname})</span>
                    </td>
                    <td className="py-3 px-4 text-zinc-700 dark:text-zinc-300 font-medium">
                      {dev.userName}
                    </td>
                    <td className="py-3 px-4 text-zinc-500">
                      {dev.userDepartment}
                    </td>
                    <td className="py-3 px-4 font-mono text-zinc-500 text-[11px]">
                      {dev.lastSeenAt ? new Date(dev.lastSeenAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Never Seen'}
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-rose-500">
                      {dev.offlineMinutes >= 60
                        ? `${Math.floor(dev.offlineMinutes / 60)}h ${dev.offlineMinutes % 60}m`
                        : `${dev.offlineMinutes}m`}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        dev.classification === 'FULL_SHIFT_ABSENT'
                          ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20'
                          : dev.classification === 'DISCONNECTED_IN_SHIFT'
                          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
                          : 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border border-zinc-500/20'
                      }`}>
                        {dev.classificationLabel}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-500">
                        <span className="w-2 h-2 rounded-full bg-rose-500" />
                        <span>OFFLINE</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Break Policy & Compliance Audit */}
      <div className="p-6 rounded-3xl ios-card space-y-4">
        <div className="flex items-center justify-between border-b border-black/5 dark:border-white/5 pb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            <h3 className="text-sm font-bold text-zinc-900 dark:text-white uppercase tracking-wider">
              Break Policy Compliance & Wellness Audit
            </h3>
          </div>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-bold">
            Policy Compliant
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
          <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 border border-black/5 dark:border-white/5 space-y-1">
            <span className="text-zinc-500 dark:text-zinc-400 block font-medium">Standard Break Protection</span>
            <div className="text-lg font-bold text-zinc-900 dark:text-white">Active & Muted</div>
            <p className="text-[11px] text-zinc-500 mt-1">
              Authorized break intervals prevent false-positive administrative alerts.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 border border-black/5 dark:border-white/5 space-y-1">
            <span className="text-zinc-500 dark:text-zinc-400 block font-medium">Zero Keylogging Verification</span>
            <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">100% Cryptographic Invariant</div>
            <p className="text-[11px] text-zinc-500 mt-1">
              All idle measurements executed via Win32 <code className="font-mono">GetLastInputInfo</code>.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/60 border border-black/5 dark:border-white/5 space-y-1">
            <span className="text-zinc-500 dark:text-zinc-400 block font-medium">Executive Audit Certificate</span>
            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">Digitally Verified</div>
            <p className="text-[11px] text-zinc-500 mt-1">
              Compliant with internal corporate HR time-auditing standards.
            </p>
          </div>
        </div>
      </div>

      {/* PDF Executive Dossier Modal */}
      <ReportModal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} />
    </div>
  );
}
